import {
    type PriceCalculationValues,
    type PriceCalculationFormula,
    type PriceModel,
    type PriceCalculationParameterInfo,
} from "../api";
import { ValueObject } from "../base";
import { ServiceParameterGroupEntity } from "../configuration/ServiceParameterGroupEntity";
import { ServiceParameterGroups } from "../configuration/ServiceParameterGroups";
import { createPriceCalculation } from "./create-price-calculation";
import { evaluateFormula } from "./evaluate-formula";
import { ResolvedServiceConfigurationPrice } from "./ResolvedServiceConfigurationPrice";

/**
 * Represents a pricing model for a service definition.
 * A price model defines how the price of a service configuration is calculated
 * based on the selected parameter values.
 */
export class PriceModelValue
    extends ValueObject(
        class {
            readonly parameterGroups: ServiceParameterGroups;
            readonly priceCalculation: PriceCalculationFormula | undefined;

            constructor(props: PriceModel) {
                this.parameterGroups = new ServiceParameterGroups(
                    props.parameterGroups?.map(group => ServiceParameterGroupEntity.create(group)) ?? []
                );
                this.priceCalculation = createPriceCalculation("CHF", this.parameterGroups);
            }
        }
    )
    implements PriceModel
{
    /**
     * Gets the parameter groups.
     *
     * @returns The parameter groups
     */
    getParameterGroups(): ServiceParameterGroups {
        return this.parameterGroups;
    }

    /**
     * Applies the user input to the price model.
     *
     * @param parameterValues The parameter values
     * @returns The service configuration price calculated by the price model
     */
    applyUserInput(parameterValues: PriceCalculationValues): ResolvedServiceConfigurationPrice {
        if (!this.priceCalculation) {
            return new ResolvedServiceConfigurationPrice({});
        }
        const price = evaluateFormula(this.priceCalculation, parameterValues);
        return new ResolvedServiceConfigurationPrice({ subTotal: price?.value });
    }

    /**
     * Gets the IDs of parameters referenced in the price calculation formula.
     * This includes REFERENCE, LOOKUP, and LIST formula types.
     *
     * @returns An array of unique parameter IDs referenced in the formula
     */
    getReferencedParameterIds(): PriceCalculationParameterInfo.Id[] {
        if (!this.priceCalculation) {
            return [];
        }
        const refs = extractReferencesRecursive(this.priceCalculation);
        return [...new Set(refs)];
    }

    /**
     * Creates a new price model with valid service definition ID.
     */
    static create(props: PriceModel): PriceModelValue | never {
        return new PriceModelValue(props);
    }
}

const extractReferencesRecursive = (formula: PriceCalculationFormula): PriceCalculationParameterInfo.Id[] => {
    switch (formula.type) {
        case "REFERENCE":
            return [formula.reference];
        case "LOOKUP":
            return [formula.reference];
        case "LIST": {
            const refs = [formula.reference];
            if (formula.calculation) {
                refs.push(...extractReferencesRecursive(formula.calculation));
            }
            return refs;
        }
        case "SUM":
        case "PROD":
        case "MIN":
        case "MAX":
            return formula.entries.flatMap(extractReferencesRecursive);
        case "INV":
            return extractReferencesRecursive(formula.entry);
        case "CONSTANT":
            return [];
    }
};
